import { useCallback, useRef } from 'react';

// Base URL for audio files
const AUDIO_BASE_URL = 'https://bkamharic.com/apps/fidel-fun/audio';

// Cache for audio objects to improve performance
const audioCache = new Map();

export const useAudio = () => {
  const currentAudioRef = useRef(null);

  // Function to get audio URL for a character
  const getAudioUrl = useCallback((character) => {
    // Handle numerals - map to numeric filenames in /numbers folder
    const numeralMap = {
      '፩': '1', '፪': '2', '፫': '3', '፬': '4', '፭': '5',
      '፮': '6', '፯': '7', '፰': '8', '፱': '9', '፲': '10',
      '፳': '20', '፴': '30', '፵': '40', '፶': '50', '፷': '60',
      '፸': '70', '፹': '80', '፺': '90', '፻': '100'
    };

    // Check if it's a numeral
    if (numeralMap[character]) {
      return `${AUDIO_BASE_URL}/numbers/${numeralMap[character]}.mp3`;
    }
    
    // For alphabet characters, use the character directly from /alphabets folder
    const fileName = character.trim();
    return `${AUDIO_BASE_URL}/alphabets/${fileName}.mp3`;
  }, []);

  // Preload audio file
  const preloadAudio = useCallback((url) => {
    if (audioCache.has(url)) {
      return audioCache.get(url);
    }

    const audio = new Audio(url);
    audio.preload = 'auto';
    
    // Handle loading errors
    audio.addEventListener('error', () => {
      console.warn(`Failed to load audio: ${url}`);
      audioCache.delete(url);
    });

    audioCache.set(url, audio);
    return audio;
  }, []);

  const playCharacterSound = useCallback((character) => {
    // Stop any currently playing audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
    }

    const audioUrl = getAudioUrl(character);
    const audio = preloadAudio(audioUrl);

    audio.currentTime = 0;
    
    const playPromise = audio.play();
    
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          currentAudioRef.current = audio;
        })
        .catch(error => {
          console.warn(`Failed to play audio for ${character}:`, error);
          // Fallback to beep sound if audio fails
          playFallbackSound();
        });
    }
  }, [getAudioUrl, preloadAudio]);

  // Fallback sound for when audio files fail to load
  const playFallbackSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5 note
      gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (error) {
      console.warn('Fallback sound failed:', error);
    }
  }, []);

  const speakText = useCallback((text) => {
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'am-ET';
      utterance.rate = 0.8;
      utterance.pitch = 1.1;
      utterance.volume = 0.9;
      
      utterance.onend = () => {
        console.log('Speech synthesis completed');
      };
      
      utterance.onerror = (event) => {
        console.warn('Speech synthesis error:', event);
      };
      
      window.speechSynthesis.speak(utterance);
    } else {
      console.log('Speech synthesis not supported in this browser');
    }
  }, []);

  // Preload commonly used audio files
  const preloadCommonSounds = useCallback(() => {
    // Preload first few alphabet characters and numbers
    const commonCharacters = ['ሀ', 'ለ', 'ሐ', 'መ', 'ሰ', 'አ', 'ከ', 'ወ', '፩', '፪', '፫'];
    commonCharacters.forEach(char => {
      const url = getAudioUrl(char);
      preloadAudio(url);
    });
  }, [getAudioUrl, preloadAudio]);

  return { 
    playCharacterSound, 
    speakText, 
    preloadCommonSounds,
    getAudioUrl // Expose for testing/debugging
  };
};